import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { CORE_CONFIG, CoreModuleConfig } from '../model/core-module-config';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly translate: TranslateService,
    private readonly spinner: NgxSpinnerService,
    @Inject(CORE_CONFIG) public config: CoreModuleConfig
  ) {}

  /** API root URL from `window.environment` when set, otherwise from the build-time environment file. */
  private get baseUrl(): string {
    const appEnvironment = (window as any).environment || environment;
    return appEnvironment.ApiUrl;
  }

  /** Builds default HTTP headers for API calls, including lang, AppId, DeviceId, and Bearer token when logged in. */
  getUploadHeaders() {
    const token: string | null = localStorage.getItem('token') || null;
    const language: string = localStorage.getItem('lang')
      ? String(localStorage.getItem('lang'))
      : this.translate.defaultLang;

    if (token) {
      return {
        headers: new HttpHeaders()
          .set('lang', language)
          .set('AppId', "4")
          .set('DeviceId', localStorage.getItem('DeviceId') ?? '')
          .set('Authorization', 'Bearer ' + token),
      };
    }
    return {
      headers: new HttpHeaders()
        .set('lang', language)
        .set('AppId', "4")
        .set('DeviceId', localStorage.getItem('DeviceId') ?? '')
    };
  }

  /** GET request against a path relative to baseUrl; drops null/undefined query params. */
  public get<TResponse>(type: string, params: any = {}): Observable<TResponse> {
    return this.http.get<TResponse>(this.baseUrl + type, {
      params: { ...this.removeNull(params) },
      ...this.getUploadHeaders(),
    });
  }

  /** POST JSON body to a path relative to baseUrl with standard headers. */
  public post<TInput, TResponse>(
    type: string,
    data: TInput
  ): Observable<TResponse> {
    return this.http.post<TResponse>(this.baseUrl + type, data ?? {}, {
      ...this.getUploadHeaders(),
    });
  }

  /** PUT JSON body with optional query params and standard headers. */
  public put<TInput, TResponse>(
    type: string,
    data: TInput,
    params?: any
  ): Observable<TResponse> {
    return this.http.put<TResponse>(this.baseUrl + type, data, {
      params: { ...params },
      ...this.getUploadHeaders(),
    });
  }

  /** Removes null, undefined, and string "null"/"undefined" entries from a params object in place. */
  private removeNull(queryParams: any) {
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] == null || queryParams[key] == undefined
        || queryParams[key] == "null" || queryParams[key] == "undefined")
        delete queryParams[key];
    });
    return queryParams;
  }
}
